import { useEffect, useMemo, useState } from "react";

import { getReportStrategyDetail } from "../../../services/reportsApi";
import type { ReportStrategyDetailResponse } from "../../../types/reports";
import type { WorkspaceNode } from "../../../types/workspace";
import {
  buildEntityCardDetailFromMeta,
  buildStrategyEntityCardDetail,
  workspaceEntityCardDetailKey,
  workspaceNodesNeedingCardDetails,
  type WorkspaceEntityCardDetailsMap,
} from "./workspaceEntityCardDetails";

/**
 * Canvas card chrome from workspace `node.meta`, with a light strategy-detail
 * overlay so description / Updated show without bulk entity fetches.
 * Full report/backtest/policy entities still load on Open / drawer drill-in.
 */
export function useWorkspaceEntityCardDetails(
  nodes: WorkspaceNode[] | undefined,
  _edges?: import("../../../types/workspace").WorkspaceEdge[]
): WorkspaceEntityCardDetailsMap {
  const strategyEntityIds = useMemo(() => {
    const ids = new Set<string>();
    for (const node of nodes ?? []) {
      if (node.type !== "strategy") continue;
      const id = node.entityId?.trim();
      if (id) ids.add(id);
    }
    return Array.from(ids).sort();
  }, [nodes]);

  const strategyIdsKey = strategyEntityIds.join("|");
  const [strategyById, setStrategyById] = useState<
    Record<string, ReportStrategyDetailResponse>
  >({});

  useEffect(() => {
    let cancelled = false;
    const ids = strategyIdsKey ? strategyIdsKey.split("|") : [];

    if (ids.length === 0) {
      setStrategyById({});
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      const fetched: Record<string, ReportStrategyDetailResponse> = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            const detail = await getReportStrategyDetail(id);
            if (detail?.id) fetched[id] = detail;
          } catch {
            // Keep meta-only chrome when detail is unavailable.
          }
        })
      );
      if (cancelled) return;
      setStrategyById(fetched);
    })();

    return () => {
      cancelled = true;
    };
  }, [strategyIdsKey]);

  return useMemo(() => {
    const next: WorkspaceEntityCardDetailsMap = {};
    for (const { type, entityId } of workspaceNodesNeedingCardDetails(nodes)) {
      const node = (nodes ?? []).find((n) => n.type === type && n.entityId === entityId);
      if (type === "strategy") {
        const strategy = strategyById[entityId];
        if (strategy) {
          next[workspaceEntityCardDetailKey(type, entityId)] =
            buildStrategyEntityCardDetail(strategy);
          continue;
        }
      }
      const detail = buildEntityCardDetailFromMeta(type, node?.meta);
      if (detail) next[workspaceEntityCardDetailKey(type, entityId)] = detail;
    }
    return next;
  }, [nodes, strategyById]);
}
